import { GoogleMap, InfoWindow, MarkerF, useJsApiLoader } from '@react-google-maps/api';
import React, { useState } from 'react';
import AdvancedMapMarker from './AdvancedMapMarker';
import MapFallback from './MapFallback';

const mapsApiKey = (import.meta.env.VITE_GOOGLE_MAPS_API_KEY || '').trim();
const mapId = (import.meta.env.VITE_GOOGLE_MAPS_MAP_ID || '').trim();
const markerLibraries = ['marker'];
const mapIds = mapId ? [mapId] : undefined;

// 100% width AND height so the map fills whatever wrapper the parent uses
// (e.g., h-[250px] md:h-[350px] on the event details page). Previously this
// was hardcoded to 400px which got clipped by the parent's overflow-hidden,
// leaving the marker partially or fully off-screen depending on zoom.
const containerStyle = {
    width: '100%',
    height: '100%',
    borderRadius: '10px',
    boxShadow: '0px 0px 10px #f3f6f9',
};

const COUNTRY_CENTERS = {
    Jamaica:        { lat: 18.1096, lng: -77.2975, zoom: 8.5 },
    'United States': { lat: 39.8283, lng: -98.5795, zoom: 4 },
};
const FALLBACK_CENTER = { lat: 39.8283, lng: -98.5795, zoom: 4 };

export default function GoogleMapDetails(props) {
    if (!mapsApiKey) {
        return <MapFallback message="Map unavailable: Google Maps key is missing." />;
    }

    return <LoadedGoogleMapDetails {...props} />;
}

function LoadedGoogleMapDetails(props) {
    const { event } = props;
    const [selectedPlace, setSelectedPlace] = useState(null);

    const { isLoaded, loadError } = useJsApiLoader({
        id: 'google-map-script',
        googleMapsApiKey: mapsApiKey,
        libraries: markerLibraries,
        mapIds,
    });

    // Parse the event's coordinates ONCE. We accept strings too (the API
    // returns them as decimal strings in JSON), and fall back gracefully
    // when either is missing or unparseable.
    const lat = parseFloat(event?.latitude);
    const lng = parseFloat(event?.longitude);
    const hasCoords = !isNaN(lat) && !isNaN(lng) && (lat !== 0 || lng !== 0);

    // Center + zoom precedence:
    //   1. If the event has real coordinates → center ON the event at
    //      street-level zoom (15). This is what makes the marker visible
    //      without the user having to pan around.
    //   2. Otherwise → country center, country-level zoom.
    //   3. Otherwise → world view.
    const fallback = COUNTRY_CENTERS[event?.country] || FALLBACK_CENTER;
    const center = hasCoords ? { lat, lng } : { lat: fallback.lat, lng: fallback.lng };
    const zoom = hasCoords ? 15 : fallback.zoom;

    const markerDetails = hasCoords
        ? {
              position: { lat, lng },
              name: event?.name,
              address: event?.venueaddress,
          }
        : null;

    const handleMarkerClick = (marker) => setSelectedPlace(marker);
    const handleInfoWindowClose = () => setSelectedPlace(null);

    if (loadError) {
        return <MapFallback message="Map unavailable: Google Maps could not load." />;
    }

    if (!isLoaded) return <MapFallback loading />;

    return (
        <div style={{ width: '100%', height: '100%' }}>
            <GoogleMap
                mapContainerStyle={containerStyle}
                zoom={zoom}
                center={center}
                options={mapId ? { mapId } : undefined}
            >
                {markerDetails && (
                    mapId ? (
                        <AdvancedMapMarker
                            position={markerDetails.position}
                            title={markerDetails.name}
                            onClick={() => handleMarkerClick(markerDetails)}
                            icon="/assets/icons/location.png"
                        />
                    ) : (
                        <MarkerF
                            position={markerDetails.position}
                            title={markerDetails.name}
                            onClick={() => handleMarkerClick(markerDetails)}
                            // Custom yellow location icon. If the file is missing
                            // (path moved on a future deploy) Google Maps falls
                            // back to the default red marker — the marker still
                            // renders, just with a different icon.
                            icon="/assets/icons/location.png"
                        />
                    )
                )}
                {selectedPlace && (
                    <InfoWindow
                        position={selectedPlace.position}
                        onCloseClick={handleInfoWindowClose}
                    >
                        <div className="info-window-content">
                            <h5>{selectedPlace.name}</h5>
                            <p>{selectedPlace.address}</p>
                        </div>
                    </InfoWindow>
                )}
            </GoogleMap>
        </div>
    );
}
